<template>
  <div class="cp-page" v-if="portraitData">
    <!-- ===== 课程身份条 ===== -->
    <div class="cp-idbar">
      <div class="cp-cover"><span class="cp-play"></span></div>
      <div class="cp-idmain">
        <div class="cp-name">《{{ lessonInfo.title || '未命名课程' }}》</div>
        <div class="cp-chips">
          <span class="cp-chip" v-if="lessonInfo.teacherName">授课教师 · {{ lessonInfo.teacherName }}</span>
          <span class="cp-chip sub" v-if="lessonInfo.subjectName">学科 · {{ lessonInfo.subjectName }}</span>
          <span class="cp-chip tmpl" v-if="lessonInfo.templateName">模板 · {{ lessonInfo.templateName }}</span>
          <span class="cp-chip" v-if="lessonInfo.className">班级 · {{ lessonInfo.className }}</span>
          <span class="cp-chip" v-if="dateText">评课时间 · {{ dateText }}</span>
        </div>
      </div>
      <div class="cp-idright">
        <span class="cp-level">{{ levelText }}</span>
        <span class="cp-update">数据更新于 <b>{{ lessonInfo.createTime || '—' }}</b></span>
      </div>
    </div>

    <!-- ===== KPI 四卡 ===== -->
    <div class="cp-kpis">
      <div class="cp-kpi" style="--c:#4fc3ff;">
        <div class="ring">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4fc3ff" stroke-width="2"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></svg>
        </div>
        <div class="info">
          <div class="label">课程总得分</div>
          <div class="value">{{ fmt(finalScore) }}<small>/{{ fmt(maxTotal) }}</small></div>
          <div class="trend">得分率 {{ pct(finalScore, maxTotal) }}%</div>
        </div>
      </div>
      <div class="cp-kpi" style="--c:#ffd24a;" v-if="hasSplit">
        <div class="ring">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffd24a" stroke-width="2"><path d="M12 2l2.9 6.3 6.9.6-5.2 4.6 1.5 6.8L12 16.9 5.9 20.3l1.5-6.8L2.2 8.9l6.9-.6z"/></svg>
        </div>
        <div class="info">
          <div class="label">专家评价</div>
          <div class="value">{{ fmt(expertTotal) }}<small v-if="maxTotal !== 100">/{{ fmt(maxTotal) }}</small></div>
          <div class="trend">{{ reviewCount ? reviewCount + ' 位专家已评课' : '暂无专家评分' }}</div>
        </div>
      </div>
      <div class="cp-kpi" style="--c:#35e0b0;" v-if="hasSplit">
        <div class="ring">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#35e0b0" stroke-width="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M8 12h8M8 9h5"/></svg>
        </div>
        <div class="info">
          <div class="label">AI 评价</div>
          <div class="value">{{ fmt(aiTotal) }}<small v-if="maxTotal !== 100">/{{ fmt(maxTotal) }}</small></div>
          <div class="trend">{{ aiExpertTrend }}</div>
        </div>
      </div>
      <div class="cp-kpi" style="--c:#a78bfa;">
        <div class="ring">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" stroke-width="2"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H19v18H6.5A2.5 2.5 0 0 1 4 18.5z"/><path d="M8 7h7M8 11h7"/></svg>
        </div>
        <div class="info">
          <div class="label">评价维度</div>
          <div class="value">{{ dims.length }}<small>维 / {{ tableData.length }} 项</small></div>
          <div class="trend">{{ dimNamesShort }}</div>
        </div>
      </div>
    </div>

    <!-- ===== 主区三栏 ===== -->
    <div class="cp-grid">
      <!-- 左栏 -->
      <div class="cp-col">
        <div class="cp-card">
          <div class="cp-sectitle">教研视频<span class="more" v-if="videoUrl">点击播放</span></div>
          <div class="cp-video">
            <AliPlayerVOD v-if="videoUrl !== ''" ref="alipayvodEl" :url="videoUrl" playerHeight="100%"></AliPlayerVOD>
            <div v-else class="cp-video-empty">
              <span class="cp-vplay"></span>
              <span class="cp-video-tip">暂无视频资源</span>
            </div>
          </div>
        </div>
        <div class="cp-card cp-flex1">
          <div class="cp-sectitle" style="--accent:#35e0b0;">总体评价<span class="more">AI 生成</span></div>
          <div class="cp-sum-block">
            <div class="cp-tag">课堂总结</div>
            <div class="cp-sumtext">{{ summary_lists[0].value }}</div>
          </div>
          <div class="cp-sum-block">
            <div class="cp-tag sg">改进建议</div>
            <div class="cp-sumtext">{{ summary_lists[1].value }}</div>
          </div>
        </div>
      </div>

      <!-- 中栏 -->
      <div class="cp-col">
        <div class="cp-card">
          <div class="cp-sectitle" style="--accent:#4fc3ff;">评分雷达<span class="more">按一级维度</span></div>
          <div class="cp-chart" v-show="dims.length"><div ref="radarChart" class="cp-echart"></div></div>
          <div class="cp-empty" v-if="!dims.length">暂无维度评分数据</div>
          <div class="cp-legend" v-if="dims.length && hasSplit">
            <span class="item"><i class="lc lc-expert"></i>专家评价</span>
            <span class="item"><i class="lc lc-ai"></i>AI 评价</span>
            <span class="item"><i class="lc lc-sum"></i>汇总</span>
          </div>
          <div class="cp-legend" v-else-if="dims.length">
            <span class="item"><i class="lc lc-sum"></i>汇总</span>
          </div>
        </div>

        <div class="cp-card" v-if="hasSplit">
          <div class="cp-sectitle" style="--accent:#ffd24a;">汇总构成<span class="more">{{ showMix ? '人机协同权重' : '折合得分率' }}</span></div>
          <template v-if="showMix">
            <div class="cp-mix-row">
              <span class="cp-mix-pill">{{ fmt(expertTotal) }}<i>专家 ×50%</i></span>
              <span class="cp-plus">+</span>
              <span class="cp-mix-pill ai">{{ fmt(aiTotal) }}<i>AI ×50%</i></span>
              <span class="cp-eq">=</span>
              <span class="cp-mix-total">{{ fmt(finalScore) }}<small>汇总</small></span>
            </div>
            <div class="cp-mix-bar">
              <div :style="{ width: expertShare + '%', background: 'linear-gradient(90deg,rgba(255,210,74,.55),#ffd24a)' }"></div>
              <div :style="{ width: aiShare + '%', background: 'linear-gradient(90deg,#4fc3ff,rgba(79,195,255,.55))' }"></div>
            </div>
            <div class="cp-mix-note">汇总得分 = 专家评价 × 50% + AI 评价 × 50%</div>
          </template>
          <template v-else>
            <div class="cp-rate-row">
              <div class="cp-rate-item">
                <span class="k">专家得分率</span>
                <span class="v expert">{{ pct(expertTotal, maxTotal) }}%</span>
              </div>
              <div class="cp-rate-item">
                <span class="k">AI 得分率</span>
                <span class="v ai">{{ pct(aiTotal, maxTotal) }}%</span>
              </div>
              <div class="cp-rate-item">
                <span class="k">汇总得分率</span>
                <span class="v sum">{{ pct(finalScore, maxTotal) }}%</span>
              </div>
            </div>
            <div class="cp-mix-bar">
              <div :style="{ width: pct(expertTotal, maxTotal) + '%', background: 'linear-gradient(90deg,rgba(255,210,74,.55),#ffd24a)' }"></div>
              <div :style="{ width: pct(aiTotal, maxTotal) + '%', background: 'linear-gradient(90deg,#4fc3ff,rgba(79,195,255,.55))' }"></div>
            </div>
            <div class="cp-mix-note">满分 {{ fmt(maxTotal) }} 分，展示各评价主体折合得分率</div>
          </template>
        </div>

        <div class="cp-card cp-flex1" v-if="hasSplit">
          <div class="cp-sectitle" style="--accent:#a78bfa;">专家 / AI 分差<span class="more">按一级维度</span></div>
          <div class="cp-diff-list">
            <div class="cp-diff-row" v-for="(d, i) in dims" :key="'d' + i">
              <span class="cp-diff-name" :title="d.name">{{ d.name }}</span>
              <div class="cp-diff-track">
                <div class="cp-diff-bar" :class="diffValue(d) >= 0 ? 'ai' : 'expert'"
                     :style="diffBarStyle(d)"></div>
              </div>
              <span class="cp-diff-val" :class="diffValue(d) >= 0 ? 'ai' : 'expert'">{{ diffText(d) }}</span>
            </div>
          </div>
          <div class="cp-diff-foot">正值 = AI 高于专家，负值 = 专家高于 AI</div>
        </div>
      </div>

      <!-- 右栏 -->
      <div class="cp-col">
        <div class="cp-card">
          <div class="cp-sectitle" style="--accent:#a78bfa;">维度得分明细<span class="more">满分 {{ fmt(maxTotal) }}</span></div>
          <div class="cp-dim-list">
            <div class="cp-dim-row" v-for="(d, i) in dims" :key="'m' + i">
              <div class="cp-dim-top">
                <span class="cp-dim-name" :title="d.name">{{ d.name }}</span>
                <span class="cp-dim-val"><b>{{ fmt(d.val) }}</b> <i>/ {{ fmt(d.max) }}</i></span>
              </div>
              <div class="cp-dim-track">
                <div class="cp-dim-fill" :class="fillClass(i)"
                     :style="{ width: pct(d.val, d.max) + '%' }"></div>
              </div>
              <div class="cp-dim-sub" v-if="hasSplit">专家 <b>{{ fmt(d.expert) }}</b> · AI <b class="ai">{{ fmt(d.machine) }}</b></div>
            </div>
            <div class="cp-empty" v-if="!dims.length">暂无维度数据</div>
          </div>
        </div>

        <div class="cp-card cp-flex1">
          <div class="cp-sectitle" style="--accent:#35e0b0;">课堂数据<span class="more">AI 分析模块</span></div>
          <div class="cp-mod-list">
            <div class="cp-mod-item" v-for="(m, i) in modules" :key="'mod' + i" @click="jumpModule(m)">
              <span class="mi">{{ m.icon }}</span>{{ m.name }}<span class="arrow">›</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 得分分析 ===== -->
    <div class="cp-card cp-mb14">
      <div class="cp-sectitle" style="--accent:#ffd24a;">得分分析<span class="more">{{ tableData.length }} 项{{ portraitData.templateLevel === 2 ? '二级' : '三级' }}指标 · 得分率</span></div>
      <div class="cp-chart" v-show="tableData.length" style="height:250px;"><div ref="barChart" class="cp-echart"></div></div>
      <div class="cp-empty" v-if="!tableData.length">暂无指标得分数据</div>
      <div class="cp-legend" v-if="tableData.length">
        <span class="item"><i class="lc lc-expert"></i>专家评价</span>
        <span class="item"><i class="lc lc-ai"></i>AI 评价</span>
      </div>
    </div>

    <!-- ===== 评课详情表 ===== -->
    <div class="cp-card cp-mb14">
      <div class="cp-sectitle">评课详情<span class="more">共 {{ tableData.length }} 项{{ (portraitData.templateLevel === 2 || portraitData.templateLevel === 5) ? '二级' : '三级' }}指标</span></div>
      <el-table :data="visibleTableData" style="width: 100%" :span-method="objectSpanMethod" class="cp-table">
        <el-table-column prop="parentName" :label="(portraitData.templateLevel===2||portraitData.templateLevel===5)?'一级指标': '二级指标'" width="130"></el-table-column>
        <el-table-column prop="name" :label="(portraitData.templateLevel===2||portraitData.templateLevel===5)?'二级指标': '三级指标'" width="130"></el-table-column>
        <el-table-column prop="expertScore" label="专家评价" width="90" align="center">
          <template slot-scope="scope">
            <span class="cp-num e">{{ scope.row.expertScore }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="machineScore" label="AI 评价" width="90" align="center">
          <template slot-scope="scope">
            <span class="cp-num">{{ scope.row.machineScore }}<i class="cp-num-max">/{{ scope.row.maxScore }}</i></span>
          </template>
        </el-table-column>
        <el-table-column prop="" label="AI 打分依据">
          <template slot-scope="scope">
            <div class="cp-reason">{{ scope.row.remark || '—' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="feedbackScore" label="评价有用性" width="160" align="center">
          <template slot-scope="scope">
            <el-rate v-model="scope.row.feedbackScore" :max="5" size="small" allow-half :disabled="ratingSubmitting || scope.row.__index !== unlockIndex"
                     @change="handleRateChange(scope.row, scope.$index)"></el-rate>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>
<script>
import * as echarts from 'echarts'
import { submit_feedback, get } from '@/request/api/lession.js'
import AliPlayerVOD from '../comp/AliplayerVOD.vue'

export default {
  name: 'PortraitLevel2',
  props: {
    portraitData: {
      type: Object,
      default: null
    },
    lessonId: {
      type: [String, Number],
      required: true
    }
  },
  components: {
    AliPlayerVOD
  },
  data() {
    return {
      finalScore: '', //总得分
      reviewCount: 0,//评课人数
      tableData: [], //表格数据
      radarData: null, //雷达数据（后端原始对象）
      summary_lists: [
        { name: '总结', value: '无' },
        { name: '建议', value: '无' },
      ],
      videoUrl: '',
      // 课堂数据（AI 分析）模块入口
      modules: [
        { name: '音量时间变化分析', icon: '🔊' },
        { name: '语速时间变化分析', icon: '⏱️' },
        { name: '教师语音综合分析', icon: '🎙️' },
        { name: '对话编码数据S-T分析', icon: '🔀' },
        { name: '教师活动情况分析', icon: '🚶' },
        { name: '教师行为表情情况分析', icon: '😊' }
      ],
      // ✅ 单条模式：一次只显示一条，评分成功才进入下一条
      unlockIndex: 0,
      ratingSubmitting: false,
      radarChartInstance: null,
      barChartInstance: null
    }
  },
  computed: {
    lessonInfo() {
      return (this.portraitData && this.portraitData.lesson) || {}
    },
    dateText() {
      const t = this.lessonInfo.reviewTime || this.lessonInfo.createTime || ''
      return t ? String(t).slice(0, 10) : ''
    },
    levelText() {
      const lv = this.portraitData ? this.portraitData.templateLevel : ''
      if (lv === 2) return '二级指标评课'
      if (lv === 3) return '三级指标评课'
      return ''
    },
    dims() {
      const r = this.radarData || {}
      const names = r.indicatorNames || []
      return names.map((name, i) => ({
        name,
        val: Number(r.totalScores && r.totalScores[i]) || 0,
        expert: Number(r.expertScores && r.expertScores[i]) || 0,
        machine: Number(r.machineScores && r.machineScores[i]) || 0,
        max: Number(r.maxScores && r.maxScores[i]) || 0
      }))
    },
    dimNamesShort() {
      return this.dims.map(d => d.name).slice(0, 4).join(' · ') || '暂无维度'
    },
    expertTotal() {
      const r = this.radarData || {}
      return this.round1((r.expertScores || []).reduce((a, b) => a + (Number(b) || 0), 0))
    },
    aiTotal() {
      const r = this.radarData || {}
      return this.round1((r.machineScores || []).reduce((a, b) => a + (Number(b) || 0), 0))
    },
    hasSplit() {
      return this.expertTotal > 0 || this.aiTotal > 0
    },
    maxTotal() {
      const r = this.radarData || {}
      const sum = (r.maxScores || []).reduce((a, b) => a + (Number(b) || 0), 0)
      return this.round1(sum) || 100
    },
    showMix() {
      return this.hasSplit && this.maxTotal === 100
    },
    expertShare() {
      const t = this.expertTotal + this.aiTotal
      return t > 0 ? this.round1((this.expertTotal / t) * 100) : 50
    },
    aiShare() {
      const t = this.expertTotal + this.aiTotal
      return t > 0 ? this.round1(100 - (this.expertTotal / t) * 100) : 50
    },
    aiExpertTrend() {
      if (!this.hasSplit) return '暂无 AI 评分'
      const diff = this.round1(this.aiTotal - this.expertTotal)
      if (diff > 0) return '较专家高 ' + this.fmt(diff) + ' 分'
      if (diff < 0) return '较专家低 ' + this.fmt(Math.abs(diff)) + ' 分'
      return '与专家持平'
    },
    visibleTableData() {
      if (!this.tableData || this.tableData.length === 0) return [];
      const i = Math.min(this.unlockIndex, this.tableData.length - 1);
      // ✅ 展示：已评价的全部 + 当前待评价的一条
      return this.tableData.slice(0, i + 1);
    },
    totalNo() {
      return this.tableData ? this.tableData.length : 0;
    },
    currentNo() {
      if (!this.tableData || this.tableData.length === 0) return 0;
      return Math.min(this.unlockIndex + 1, this.tableData.length);
    }
  },
  watch: {
    portraitData: {
      handler(newData) {
        if (newData) {
          this.processData(newData);
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.getLesson();
  },
  beforeDestroy() {
    if (this.radarChartInstance) { this.radarChartInstance.dispose(); this.radarChartInstance = null; }
    if (this.barChartInstance) { this.barChartInstance.dispose(); this.barChartInstance = null; }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // ===== 数值工具 =====
    round1(v) { return Math.round((Number(v) || 0) * 10) / 10; },
    fmt(v) {
      const n = Number(v);
      if (isNaN(n)) return '—';
      return String(Math.round(n * 10) / 10);
    },
    pct(v, max) {
      const m = Number(max) || 0;
      if (!m) return 0;
      return this.round1((Number(v) || 0) / m * 100);
    },
    diffValue(d) { return this.round1((d.machine || 0) - (d.expert || 0)); },
    diffText(d) {
      const v = this.diffValue(d);
      return (v > 0 ? '+' : '') + this.fmt(v);
    },
    diffBarStyle(d) {
      const v = this.diffValue(d);
      const max = Math.max(Number(d.max) || 1, 1);
      const w = Math.min(50, Math.abs(v) / max * 100 * 2);
      return v >= 0 ? { left: '50%', width: w + '%' } : { right: '50%', width: w + '%' };
    },
    fillClass(i) {
      const arr = ['', 'green', 'purple', 'gold'];
      return arr[i % arr.length];
    },
    jumpModule(m) {
      this.$emit('jump-module', m.name);
    },

    // 表格合并行方法
    objectSpanMethod({ row, column, rowIndex, columnIndex }) {
      const dataList = this.visibleTableData;
      if (columnIndex === 0) {
        const currentValue = row.parentName;
        const prevValue = rowIndex > 0 ? dataList[rowIndex - 1].parentName : null;
        if (prevValue === currentValue) {
          return { rowspan: 0, colspan: 0 };
        }
        let rowspan = 1;
        for (let i = rowIndex + 1; i < dataList.length; i++) {
          if (dataList[i].parentName === currentValue) {
            rowspan++;
          } else {
            break;
          }
        }
        return { rowspan: rowspan, colspan: 1 };
      }
    },

    processData(data) {
      this.finalScore = data.finalScore || '';
      this.reviewCount = data.reviewCount || 0;
      this.tableData = (data.tableData || []).map((item, __index) => ({
        ...item,
        __index,
        feedbackScore: (item.feedbackScore !== undefined && item.feedbackScore !== null) ? item.feedbackScore : null
      }));
      this.computeUnlockIndex();
      this.radarData = data.radarData;

      //建议/总结
      const summary = data ? data.summary : null;
      if (summary) {
        this.summary_lists[0].value = summary.summary || '无';
        this.summary_lists[1].value = summary.suggest || '无';
      } else {
        this.summary_lists[0].value = '无';
        this.summary_lists[1].value = '无';
      }

      this.$nextTick(() => {
        this.renderRadar();
        this.renderBar();
      });
    },

    // ===== 图表 =====
    handleResize() {
      if (this.radarChartInstance) this.radarChartInstance.resize();
      if (this.barChartInstance) this.barChartInstance.resize();
    },
    renderRadar() {
      const el = this.$refs.radarChart;
      if (!el || !this.dims.length) return;
      if (this.radarChartInstance) { this.radarChartInstance.dispose(); }
      this.radarChartInstance = echarts.init(el);
      const indicators = this.dims.map(d => ({ name: d.name, max: d.max || 100 }));
      const seriesData = [];
      if (this.hasSplit) {
        seriesData.push({
          name: '专家评价', value: this.dims.map(d => d.expert),
          lineStyle: { color: '#ffd24a', width: 2 },
          itemStyle: { color: '#ffd24a' },
          areaStyle: { color: 'rgba(255,210,74,.14)' }, symbolSize: 4
        });
        seriesData.push({
          name: 'AI 评价', value: this.dims.map(d => d.machine),
          lineStyle: { color: '#4fc3ff', width: 2 },
          itemStyle: { color: '#4fc3ff' },
          areaStyle: { color: 'rgba(79,195,255,.20)' }, symbolSize: 4
        });
      }
      seriesData.push({
        name: '汇总', value: this.dims.map(d => d.val),
        lineStyle: { color: '#a78bfa', width: 1.5, type: 'dashed' },
        itemStyle: { color: '#a78bfa' },
        areaStyle: { color: this.hasSplit ? 'rgba(167,139,250,.08)' : 'rgba(167,139,250,.22)' },
        symbolSize: this.hasSplit ? 0 : 4
      });
      this.radarChartInstance.setOption({
        tooltip: {
          confine: true,
          backgroundColor: 'rgba(6,22,54,.95)',
          borderColor: 'rgba(79,195,255,.5)',
          borderWidth: 1,
          textStyle: { color: '#cfe6ff', fontSize: 12 },
          formatter: (p) => {
            const idx = p.dataIndex != null ? p.dataIndex : 0;
            const d = this.dims[idx] || {};
            let html = '<b>' + (d.name || '') + '</b>';
            html += '<div>汇总：' + this.fmt(d.val) + ' / ' + this.fmt(d.max) + '</div>';
            if (this.hasSplit) {
              html += '<div>专家：' + this.fmt(d.expert) + '</div>';
              html += '<div>AI：' + this.fmt(d.machine) + '</div>';
            }
            return html;
          }
        },
        radar: {
          radius: '62%',
          center: ['50%', '54%'],
          axisName: { color: '#a9c6e8', fontSize: 11 },
          angleLines: { color: 'rgba(63,156,255,.22)' },
          splitLine: { lineStyle: { color: 'rgba(63,156,255,.22)' } },
          splitArea: { areaStyle: { color: ['rgba(21,113,231,.04)', 'rgba(21,113,231,.10)'] } },
          indicator: indicators
        },
        series: [{ type: 'radar', data: seriesData }]
      }, true);
      window.removeEventListener('resize', this.handleResize);
      window.addEventListener('resize', this.handleResize);
    },
    renderBar() {
      const el = this.$refs.barChart;
      if (!el || !this.tableData.length) return;
      if (this.barChartInstance) { this.barChartInstance.dispose(); }
      this.barChartInstance = echarts.init(el);
      const labels = this.tableData.map(r => r.name || '');
      const eRates = this.tableData.map(r => this.pct(r.expertScore, r.maxScore));
      const aRates = this.tableData.map(r => this.pct(r.machineScore, r.maxScore));
      const series = [];
      if (this.hasSplit) {
        series.push({ name: '专家评价', type: 'bar', data: eRates, itemStyle: { color: 'rgba(255,210,74,.85)', borderRadius: [3, 3, 0, 0] }, barPercentage: .82, categoryPercentage: .72 });
        series.push({ name: 'AI 评价', type: 'bar', data: aRates, itemStyle: { color: 'rgba(79,195,255,.85)', borderRadius: [3, 3, 0, 0] }, barPercentage: .82, categoryPercentage: .72 });
      } else {
        series.push({ name: '得分率', type: 'bar', data: aRates, itemStyle: { color: 'rgba(79,195,255,.85)', borderRadius: [3, 3, 0, 0] }, barPercentage: .6, categoryPercentage: .7 });
      }
      this.barChartInstance.setOption({
        grid: { left: 46, right: 16, top: 16, bottom: 62 },
        tooltip: {
          confine: true,
          backgroundColor: 'rgba(6,22,54,.95)',
          borderColor: 'rgba(79,195,255,.5)',
          borderWidth: 1,
          textStyle: { color: '#cfe6ff', fontSize: 12 },
          valueFormatter: (v) => v + '%'
        },
        xAxis: {
          type: 'category',
          data: labels,
          axisLabel: { color: '#6f93c4', fontSize: 10.5, interval: 0, rotate: labels.length > 12 ? 42 : 0 },
          axisLine: { lineStyle: { color: 'rgba(63,156,255,.14)' } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value', min: 0, max: 100,
          axisLabel: { color: '#6f93c4', fontSize: 11, formatter: '{value}%' },
          splitLine: { lineStyle: { color: 'rgba(63,156,255,.14)' } },
          axisLine: { show: false }
        },
        series
      }, true);
      window.removeEventListener('resize', this.handleResize);
      window.addEventListener('resize', this.handleResize);
    },

    // ✅ 单条模式：找到第一条未评价的指标作为起点
    computeUnlockIndex() {
      const idx = (this.tableData || []).findIndex(r => r.feedbackScore === null || r.feedbackScore === undefined);
      this.unlockIndex = idx === -1 ? Math.max(0, (this.tableData || []).length - 1) : idx;
    },

    // 处理评分变化（✅ 单条模式：评分成功才进入下一条）
    handleRateChange(item, index) {
      if (this.ratingSubmitting) return;
      this.ratingSubmitting = true;

      const currentIndex = Math.min(this.unlockIndex, (this.tableData || []).length - 1);
      const currentItem = (this.tableData || [])[currentIndex] || item;

      const formData = {
        lessonId: this.lessonId,
        groupId: currentItem.groupId,
        itemId: currentItem.id,
        score: currentItem.feedbackScore,
        comment: currentItem.feedbackComment || ''
      };

      submit_feedback(formData).then(() => {
        const remaining = (this.tableData || []).filter(r =>
            r.feedbackScore === null || r.feedbackScore === undefined
        ).length;

        this.$notify({
          title: '成功',
          message: remaining > 0 ? `已评价，进入下一条；剩余 ${remaining} 条` : '已评价；剩余 0 条（全部完成）',
          type: 'success'
        });

        let next = currentIndex + 1;
        while (
            next < this.tableData.length &&
            this.tableData[next].feedbackScore !== null &&
            this.tableData[next].feedbackScore !== undefined
            ) {
          next++;
        }
        if (next < this.tableData.length) {
          this.unlockIndex = next;
        }
      }).catch(() => {
        this.$notify.error({ title: '错误', message: '评分提交失败，仍停留在当前条' });
      }).finally(() => {
        this.ratingSubmitting = false;
      });
    },

    //课程详情
    getLesson() {
      get(this.lessonId).then(res => {
        if (res && res.data) {
          this.videoUrl = res.data.videoUrl || '';
        }
      }).catch(err => {
        console.error('获取课程详情失败:', err);
      });
    }
  }
};
</script>
<style scoped lang="less">
@c-blue: #4fc3ff;
@c-gold: #ffd24a;
@c-green: #35e0b0;
@c-purple: #a78bfa;
@c-text: #cfe6ff;
@c-sub: #7ea6d9;
@card-bg: linear-gradient(180deg, rgba(13, 40, 95, .35), rgba(5, 18, 45, .55));
@card-border: 1px solid rgba(63, 156, 255, .28);

.cp-page {
  width: 100%;
  padding: 0 4px 18px;
  box-sizing: border-box;
  color: @c-text;
  font-size: 13px;
}

/* ===== 身份条 ===== */
.cp-idbar {
  display: flex;
  align-items: center;
  gap: 18px;
  background: linear-gradient(180deg, rgba(16, 55, 130, .4), rgba(6, 20, 50, .55));
  border: 1px solid rgba(63, 156, 255, .3);
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 14px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute; left: -60px; top: -80px;
    width: 260px; height: 260px; border-radius: 50%;
    background: radial-gradient(circle, rgba(42, 125, 232, .28), transparent 68%);
  }
}

.cp-cover {
  flex: none; width: 104px; height: 66px; border-radius: 8px; position: relative; z-index: 2;
  background: linear-gradient(150deg, rgba(42, 125, 232, .55), rgba(8, 26, 64, .9));
  border: 1px solid rgba(79, 195, 255, .55);
  box-shadow: 0 0 18px rgba(42, 125, 232, .45);
  display: flex; align-items: center; justify-content: center;
}

.cp-play {
  width: 32px; height: 32px; border-radius: 50%;
  border: 1px solid rgba(79, 195, 255, .8);
  background: rgba(6, 22, 54, .6);
  display: flex; align-items: center; justify-content: center;

  &::after {
    content: ""; width: 0; height: 0;
    border-left: 10px solid #6fc4ff;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    margin-left: 3px;
  }
}

.cp-idmain { flex: 1; min-width: 0; position: relative; z-index: 2; }

.cp-name {
  font-size: 22px; font-weight: 500; color: #eaf6ff; letter-spacing: 1px;
  text-shadow: 0 0 16px rgba(64, 156, 255, .6); margin-bottom: 9px;
}

.cp-chips { display: flex; flex-wrap: wrap; gap: 8px; }

.cp-chip {
  font-size: 12px; padding: 3px 12px; border-radius: 20px;
  border: 1px solid rgba(63, 156, 255, .4);
  background: rgba(27, 98, 224, .16); color: #9ec7ff;

  &.sub { color: @c-green; border-color: rgba(53, 224, 176, .45); background: rgba(53, 224, 176, .1); }
  &.tmpl { color: @c-purple; border-color: rgba(167, 139, 250, .45); background: rgba(167, 139, 250, .12); }
}

.cp-idright {
  flex: none; display: flex; flex-direction: column; align-items: flex-end; gap: 8px;
  position: relative; z-index: 2;
}

.cp-level {
  font-size: 12px; color: @c-blue; padding: 4px 12px; border-radius: 6px;
  border: 1px solid rgba(79, 195, 255, .5); background: rgba(27, 98, 224, .18);
}

.cp-update { font-size: 11px; color: #5f8fd0; b { color: @c-sub; font-weight: 400; } }

/* ===== KPI ===== */
.cp-kpis {
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
}

.cp-kpi {
  flex: 1;
  min-width: 0;
  display: flex; align-items: center; gap: 14px;
  background: linear-gradient(180deg, rgba(16, 55, 130, .35), rgba(8, 25, 60, .35));
  border: 1px solid rgba(79, 195, 255, .28);
  border-radius: 8px; padding: 12px 16px; position: relative; overflow: hidden;

  &::after {
    content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
    background: var(--c); box-shadow: 0 0 10px var(--c);
  }

  &::before {
    content: ""; position: absolute; right: -30px; top: -30px;
    width: 90px; height: 90px; border-radius: 50%;
    background: radial-gradient(circle, rgba(79, 195, 255, .18), transparent 70%);
  }

  .ring {
    flex: none; width: 46px; height: 46px; border-radius: 50%;
    background: radial-gradient(circle, rgba(8, 25, 60, .9), rgba(8, 25, 60, .9));
    border: 1px solid var(--c);
    box-shadow: 0 0 14px rgba(79, 195, 255, .25), inset 0 0 12px rgba(79, 195, 255, .18);
    display: flex; align-items: center; justify-content: center;
  }

  .info {
    min-width: 0;

    .label { font-size: 12px; color: @c-sub; margin-bottom: 2px; }

    .value {
      font-size: 24px; font-weight: 500; font-family: "DIN Alternate", "Segoe UI", Consolas, sans-serif;
      background: linear-gradient(180deg, #ffffff, var(--c));
      -webkit-background-clip: text; background-clip: text; color: transparent;
      white-space: nowrap;
      small { font-size: 12px; margin-left: 2px; }
    }

    .trend { font-size: 11px; margin-top: 2px; color: #8fb4dd; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }
}

/* ===== 卡片 ===== */
.cp-card {
  background: @card-bg;
  border: @card-border;
  border-radius: 8px;
  padding: 13px 15px;
  box-shadow: inset 0 0 24px rgba(20, 70, 170, .15);
  margin-bottom: 14px;
  min-width: 0;
}

.cp-mb14 { margin-bottom: 14px; }
.cp-flex1 { flex: 1; min-height: 0; display: flex; flex-direction: column; }

.cp-sectitle {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 500; color: #dceeff; letter-spacing: 1px;
  margin-bottom: 11px; padding-bottom: 8px;
  border-bottom: 1px solid transparent;
  border-image: linear-gradient(90deg, rgba(63, 156, 255, .8), transparent) 1;

  &::before {
    content: ""; width: 8px; height: 8px; background: var(--accent, #3f9cff);
    transform: rotate(45deg); box-shadow: 0 0 8px var(--accent, #3f9cff);
  }

  .more { margin-left: auto; font-size: 12px; color: #5f8fd0; font-weight: 400; white-space: nowrap; }
}

.cp-grid {
  display: grid;
  grid-template-columns: 30fr 44fr 26fr;
  gap: 14px;
  margin-bottom: 0;
}

.cp-col { display: flex; flex-direction: column; min-width: 0; }
.cp-col > .cp-card:last-child { margin-bottom: 14px; }

/* 视频 */
.cp-video {
  position: relative; width: 100%; height: 178px; border-radius: 6px; overflow: hidden;
  background: linear-gradient(150deg, rgba(20, 60, 120, .7), rgba(4, 14, 36, .95));
  border: 1px solid rgba(63, 156, 255, .28);
  display: flex; align-items: center; justify-content: center;

  /deep/ #J_prismPlayer, /deep/ .prism-player, /deep/ video {
    width: 100% !important; height: 100% !important;
  }
}

.cp-video-empty {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
}

.cp-vplay {
  width: 48px; height: 48px; border-radius: 50%;
  border: 1px solid rgba(79, 195, 255, .85);
  background: rgba(6, 22, 54, .55);
  box-shadow: 0 0 20px rgba(79, 195, 255, .5);
  display: flex; align-items: center; justify-content: center;

  &::after {
    content: ""; width: 0; height: 0;
    border-left: 14px solid #6fc4ff;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    margin-left: 4px;
  }
}

.cp-video-tip { font-size: 12px; color: #5f8fd0; }

/* 总体评价 */
.cp-sum-block { margin-bottom: 12px; &:last-child { margin-bottom: 0; } }

.cp-tag {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; color: @c-green; margin-bottom: 6px;

  &.sg { color: @c-gold; }

  &::before {
    content: ""; width: 6px; height: 6px; border-radius: 1px;
    background: currentColor; box-shadow: 0 0 8px currentColor;
  }
}

.cp-sumtext {
  font-size: 12.5px; line-height: 1.9; color: #c4dcf5; text-align: justify;
  white-space: pre-wrap;
  max-height: 300px; overflow-y: auto; padding-right: 4px;
}

/* 图表 */
.cp-chart { position: relative; width: 100%; height: 230px; }
.cp-echart { width: 100%; height: 100%; }

.cp-legend {
  display: flex; justify-content: center; gap: 20px; padding: 6px 0 2px; font-size: 12px;

  .item { display: flex; align-items: center; gap: 6px; color: #a9c6e8; }
  .lc { width: 18px; height: 3px; border-radius: 2px; display: inline-block; }
  .lc-expert { background: @c-gold; box-shadow: 0 0 8px @c-gold; }
  .lc-ai { background: @c-blue; box-shadow: 0 0 8px @c-blue; }
  .lc-sum { background: @c-purple; box-shadow: 0 0 8px @c-purple; }
}

.cp-empty {
  padding: 22px 0; text-align: center; font-size: 12px; color: #5f8fd0;
  border: 1px dashed rgba(63, 156, 255, .25); border-radius: 6px;
}

/* 汇总构成 */
.cp-mix-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }

.cp-mix-pill {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 2px;
  padding: 7px 4px; border-radius: 6px;
  border: 1px solid rgba(255, 210, 74, .35); background: rgba(255, 210, 74, .08);
  color: @c-gold; font-size: 15px; font-weight: 600;

  i { font-style: normal; font-size: 11px; color: #8fb4dd; font-weight: 400; }

  &.ai { border-color: rgba(79, 195, 255, .35); background: rgba(79, 195, 255, .08); color: @c-blue; }
}

.cp-plus, .cp-eq { color: #5f8fd0; font-size: 15px; }

.cp-mix-total {
  font-size: 22px; font-weight: 700; color: #fff;
  text-shadow: 0 0 12px rgba(167, 139, 250, .8); text-align: center;
  small { display: block; font-size: 10px; color: #8fb4dd; font-weight: 400; letter-spacing: 2px; }
}

.cp-mix-bar { display: flex; height: 8px; border-radius: 4px; overflow: hidden; background: rgba(63, 156, 255, .12); }
.cp-mix-note { margin-top: 8px; font-size: 11px; color: #5f8fd0; text-align: center; }

.cp-rate-row { display: flex; justify-content: space-between; gap: 10px; margin-bottom: 10px; }

.cp-rate-item {
  flex: 1; text-align: center;
  .k { display: block; font-size: 11px; color: @c-sub; margin-bottom: 3px; }
  .v { font-size: 17px; font-weight: 600; font-family: "DIN Alternate", Consolas, sans-serif; }
  .v.expert { color: @c-gold; }
  .v.ai { color: @c-blue; }
  .v.sum { color: @c-purple; }
}

/* 分差 */
.cp-diff-list { flex: 1; min-height: 0; overflow-y: auto; padding-right: 4px; }

.cp-diff-row { display: flex; align-items: center; gap: 8px; margin-bottom: 9px; }
.cp-diff-name {
  width: 62px; flex: none; font-size: 12px; color: #a9c6e8;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.cp-diff-track {
  flex: 1; position: relative; height: 6px; border-radius: 3px; background: rgba(63, 156, 255, .10);

  &::before {
    content: ""; position: absolute; left: 50%; top: -4px; bottom: -4px; width: 1px;
    background: rgba(63, 156, 255, .4);
  }
}

.cp-diff-bar {
  position: absolute; top: 0; height: 100%; border-radius: 3px;
  &.expert { background: linear-gradient(270deg, @c-gold, #ffdf7e); box-shadow: 0 0 8px rgba(255, 210, 74, .6); }
  &.ai { background: linear-gradient(90deg, @c-blue, #7fd8ff); box-shadow: 0 0 8px rgba(79, 195, 255, .6); }
}

.cp-diff-val {
  width: 44px; flex: none; text-align: right; font-size: 12px; font-weight: 600;
  &.expert { color: @c-gold; }
  &.ai { color: @c-blue; }
}

.cp-diff-foot {
  margin-top: 9px; padding-top: 8px;
  border-top: 1px dashed rgba(63, 156, 255, .22);
  font-size: 11px; color: #5f8fd0; text-align: center;
}

/* 维度明细 */
.cp-dim-list { flex: 1; min-height: 0; max-height: 340px; overflow-y: auto; padding-right: 4px; }
.cp-dim-row { margin-bottom: 12px; &:last-child { margin-bottom: 0; } }

.cp-dim-top { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 5px; }
.cp-dim-name {
  font-size: 13px; color: #dceeff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 62%;
}
.cp-dim-val {
  font-family: Consolas, monospace; font-size: 13px; color: #6fc4ff;
  b { font-size: 15px; }
  i { font-style: normal; color: #5f8fd0; font-size: 11px; }
}

.cp-dim-track { height: 9px; border-radius: 5px; background: rgba(63, 156, 255, .13); overflow: hidden; }

.cp-dim-fill {
  height: 100%; border-radius: 5px;
  background: linear-gradient(90deg, #1b62e0, @c-blue);
  box-shadow: 0 0 8px rgba(79, 195, 255, .65);
  transition: width .4s ease;

  &.gold { background: linear-gradient(90deg, #b8860b, @c-gold); box-shadow: 0 0 8px rgba(255, 210, 74, .7); }
  &.purple { background: linear-gradient(90deg, #6b46c1, @c-purple); box-shadow: 0 0 8px rgba(167, 139, 250, .7); }
  &.green { background: linear-gradient(90deg, #0f8f6a, @c-green); box-shadow: 0 0 8px rgba(53, 224, 176, .7); }
}

.cp-dim-sub {
  font-size: 11px; color: #6f93c4; margin-top: 4px;
  b { color: @c-gold; font-weight: 500; }
  b.ai { color: #6fc4ff; }
}

/* 课堂数据模块 */
.cp-mod-list { display: flex; flex-direction: column; gap: 8px; }

.cp-mod-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; border-radius: 6px; cursor: pointer;
  transition: all .2s;
  background: rgba(13, 40, 95, .35);
  border: 1px solid rgba(63, 156, 255, .2);
  font-size: 12.5px; color: #a9c6e8;

  &:hover { border-color: rgba(79, 195, 255, .6); color: #eaf6ff; background: rgba(27, 98, 224, .25); }

  .mi {
    flex: none; width: 22px; height: 22px; border-radius: 5px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(27, 98, 224, .25); border: 1px solid rgba(63, 156, 255, .35); font-size: 12px;
  }

  .arrow { margin-left: auto; color: #4f77ab; }
}

/* 表格 */
.cp-table {
  /deep/ .el-table, /deep/ .el-table__expanded-cell { background: transparent; }
  /deep/ tr, /deep/ th.el-table__cell, /deep/ td.el-table__cell { background: transparent !important; }
  /deep/ .el-table tr { background: rgba(13, 40, 95, .28) !important; }
  /deep/ .el-table tr:hover > td.el-table__cell { background: rgba(63, 156, 255, .12) !important; }
  /deep/ th.el-table__cell {
    background: linear-gradient(180deg, rgba(30, 85, 190, .5), rgba(12, 38, 90, .5)) !important;
    color: #bfe0ff; font-size: 12px; font-weight: 500; letter-spacing: 1px;
    border-bottom: 1px solid rgba(79, 195, 255, .6) !important;
    padding: 9px 0;
  }
  /deep/ th.el-table__cell > .cell {
    background: none !important; -webkit-background-clip: initial !important;
    background-clip: initial !important; color: #bfe0ff !important;
  }
  /deep/ td.el-table__cell {
    border-bottom: 1px solid rgba(63, 156, 255, .12) !important;
    color: #c4dcf5; font-size: 12.5px !important; padding: 9px 0; vertical-align: middle;
  }
  /deep/ .el-table__body-wrapper { max-height: none; }
  /deep/ .el-table::before { display: none; }
}

.cp-num {
  font-family: Consolas, monospace; font-size: 13px; color: #6fc4ff;
  &.e { color: @c-gold; }
  .cp-num-max { font-style: normal; color: #5f8fd0; font-size: 11px; }
}

.cp-reason { white-space: pre-line; color: #93b6de; font-size: 12px; line-height: 1.55; }

/* 滚动条 */
.cp-page ::-webkit-scrollbar { width: 6px; height: 6px; }
.cp-page ::-webkit-scrollbar-thumb { background: rgba(63, 156, 255, .35); border-radius: 3px; }
.cp-page ::-webkit-scrollbar-track { background: transparent; }
</style>
